/**
 * §P.6 · Los avisos del club.
 *
 * ## Publicar es interrumpir a 120 familias
 *
 * Y de ahí salen casi todas las decisiones de estas pantallas. Un aviso llega a un
 * móvil que está en el bolso de alguien que está trabajando: por eso el alcance se
 * dice **antes** de publicar y no después, por eso `reach` cuenta familias y no
 * alumnos, y por eso el borrador existe.
 *
 * ## Las audiencias se suman, no se cruzan
 *
 * «Los del martes» **y** «los cinturones verdes» llega a la unión de los dos, no a
 * la intersección. Es como lo dice un maestro en voz alta, y es lo contrario de lo
 * que sugiere un formulario con dos filtros. La pantalla lo escribe con esa
 * palabra: «se suman».
 */

/**
 * A quién va.
 *
 * Cada uno resuelve una frase que un maestro dice de verdad: «a los del martes» es
 * `group`, «a los que se examinan» es `belt_rank`, y **`debtors` ahorra la
 * conversación más incómoda del mes** — la que hoy se tiene en la puerta y delante
 * del niño.
 */
export type AmbitoDeAudiencia =
  | 'all'
  | 'group'
  | 'venue'
  | 'belt_rank'
  | 'debtors'
  | 'competitors'
  | 'discipline';

export const AMBITOS: readonly {
  valor: AmbitoDeAudiencia;
  etiqueta: string;
  /** Qué hay que elegir además, si es que hay que elegir algo. */
  pide: 'nada' | 'grupo' | 'sede' | 'cinturon' | 'disciplina';
}[] = [
  { valor: 'all', etiqueta: 'Todo el club', pide: 'nada' },
  { valor: 'group', etiqueta: 'Un grupo', pide: 'grupo' },
  { valor: 'venue', etiqueta: 'Una sede', pide: 'sede' },
  { valor: 'belt_rank', etiqueta: 'Un cinturón', pide: 'cinturon' },
  { valor: 'discipline', etiqueta: 'Una disciplina', pide: 'disciplina' },
  { valor: 'debtors', etiqueta: 'Quien debe dinero', pide: 'nada' },
  { valor: 'competitors', etiqueta: 'Los que compiten', pide: 'nada' },
];

export interface Audiencia {
  readonly scope: AmbitoDeAudiencia;
  readonly group_id: string | null;
  readonly group_name: string | null;
  readonly venue_id: string | null;
  readonly venue_name: string | null;
  readonly belt_rank_id: string | null;
  readonly belt_rank_name: string | null;
  readonly discipline_id: string | null;
  readonly discipline_name: string | null;
}

export interface Aviso {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly audiences: readonly Audiencia[];
  readonly requires_ack: boolean;
  readonly pinned: boolean;
  /** `null` es **un borrador**: la diferencia entre escribirlo y estar en 120 móviles. */
  readonly published_at: Date | string | null;
  readonly expires_at: Date | string | null;
  readonly author_name: string | null;
  /** A cuántas **familias** llega, no a cuántos alumnos. Ver la cabecera. */
  readonly reach: number;
  readonly read_count: number;
  readonly created_at: Date | string;
}

export interface AcuseDeAviso {
  readonly user_id: string;
  readonly name: string;
  readonly email: string | null;
  readonly students: readonly string[];
  readonly delivered_at: Date | string | null;
  readonly read_at: Date | string | null;
  readonly acknowledged_at: Date | string | null;
}

export interface ResumenDeAcuses {
  readonly announcement_id: string;
  readonly reach: number;
  readonly read: number;
  readonly acknowledged: number;
  readonly receipts: readonly AcuseDeAviso[];
}

/**
 * Cómo se lee una audiencia en una línea.
 *
 * El nombre de lo concreto cuando lo hay —«Infantil L-X», no «Un grupo»—, porque un
 * aviso mandado al grupo equivocado se descubre leyendo esta línea.
 */
export function etiquetaDeAudiencia(a: Audiencia): string {
  switch (a.scope) {
    case 'all':
      return 'Todo el club';
    case 'group':
      return a.group_name ?? 'Un grupo';
    case 'venue':
      return a.venue_name ?? 'Una sede';
    case 'belt_rank':
      return a.belt_rank_name ?? 'Un cinturón';
    case 'discipline':
      return a.discipline_name ?? 'Una disciplina';
    case 'debtors':
      return 'Quien debe dinero';
    case 'competitors':
      return 'Los que compiten';
    default:
      return a.scope;
  }
}

/** Un aviso está publicado si tiene fecha de publicación. Lo demás es borrador. */
export const estaPublicado = (a: Aviso): boolean => a.published_at !== null;

/**
 * Si ya caducó.
 *
 * Un aviso caducado **no se borra**: sigue en la lista, apagado. Lo que se anunció
 * se anunció, y el club tiene que poder mirar atrás y ver qué se dijo en octubre.
 */
export function estaCaducado(
  a: Aviso,
  ahora: Date | number = Date.now()
): boolean {
  if (!a.expires_at) return false;
  const fin =
    a.expires_at instanceof Date ? a.expires_at : new Date(a.expires_at);
  if (Number.isNaN(fin.getTime())) return false;
  return fin.getTime() < (ahora instanceof Date ? ahora.getTime() : ahora);
}

/**
 * Cuántos lo han leído, en porcentaje.
 *
 * `null` cuando no llegó a nadie: «0 de 0» es 0 %, y pintar un 0 % rojo en un aviso
 * que no tenía destinatarios acusa a alguien de algo que no ha pasado.
 */
export function porcentajeLeido(a: {
  reach: number;
  read_count: number;
}): number | null {
  if (a.reach <= 0) return null;
  return Math.round((a.read_count / a.reach) * 100);
}
